import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { Icon, InlineButton } from '../../..'
import { useTranslation } from '../../../../utils/providers'

import { MultiCheckbox, ErrorMessage } from '..'

function CustomSelectbox({
  data,
  label,
  disabled,
  hidden,
  options,
  multiple,
  onChange,
  className,
  placeholder,
  renderLabel,
  renderOptions,
  align,
  arrowIcon = 'cf-chevron-down',
  disableStretch = false,
}) {
  const { t } = useTranslation()
  const ref = useRef(null)
  const [isActive, setActive] = useState(false)
  const [valueState, setValueState] = useState(
    multiple ? data.value || [] : data.value
  )
  const toggleDropdown = () => setActive(!isActive)
  const hideDropdown = () => setActive(false)

  const handleChange = (e) => {
    setValueState(e.target.value)

    onChange(e)

    // Close immediately for single selections, keep open for multi selections
    if (!multiple) {
      hideDropdown()
    }
  }

  // Watch for prop changes
  useEffect(() => {
    if (valueState !== data.value) {
      setValueState(data.value)
    }
  }, [data.value])

  // Close dropdown when clicking outside the component or loosing focus
  useEffect(() => {
    const handleKeyUp = (e) => {
      if (
        e.code === 'Escape' ||
        (e.code === 'Tab' &&
          ref.current &&
          !ref.current.contains(document.activeElement))
      ) {
        hideDropdown()
        window.removeEventListener('keyup', handleKeyUp)
        window.removeEventListener('click', handleOutsideClick)
      }
    }

    const handleOutsideClick = ({ target }) => {
      if (ref.current && !ref.current.contains(target)) {
        hideDropdown()
        window.removeEventListener('click', handleOutsideClick)
        window.removeEventListener('keyup', handleKeyUp)
      }
    }

    if (isActive) {
      window.addEventListener('click', handleOutsideClick)
      window.addEventListener('keyup', handleKeyUp)
    } else {
      window.removeEventListener('click', handleOutsideClick)
      window.removeEventListener('keyup', handleKeyUp)
    }
    return () => {
      window.removeEventListener('click', handleOutsideClick)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [isActive])

  const wrapperClasses = classNames(
    'custom-select-box md-input-group',
    className,
    {
      error: !!data.error,
      'custom-select-box--disabled': disabled,
      'custom-select-box--active': isActive,
      [`text-${align}`]: ['left', 'center', 'right'].includes(align),
    }
  )

  const currentSelectionLabel = multiple
    ? t('FORM_MULTISELECT_COUNT')(valueState?.length)
    : options.find(({ value }) => valueState === value)?.label

  const renderOpts = {
    value: valueState,
    data,
    options,
    handleChange,
    isActive,
  }

  return (
    <div ref={ref} className={wrapperClasses} hidden={hidden || null}>
      {label && typeof renderLabel !== 'function' && (
        <div className="label-box" style={{ pointerEvents: 'none' }}>
          <label
            className={
              valueState?.length > 0 || placeholder ? 'float-above' : null
            }
          >
            {label}
          </label>
        </div>
      )}

      <InlineButton
        className="custom-select-box__label form-control"
        onClick={toggleDropdown}
        aria-expanded={isActive}
        aria-haspopup="listbox"
        role="combobox"
        disabled={disabled || null}
      >
        {typeof renderLabel === 'function' ? (
          renderLabel(renderOpts)
        ) : (
          <div className="d-flex justify-content-between align-items-center">
            <span>
              {/* Keep span for flex magic */}
              {valueState?.length > 0 // string or array depending on `multiple`
                ? currentSelectionLabel
                : placeholder}
            </span>
            <Icon symbol={arrowIcon} className="ml-2" />
          </div>
        )}

        {!disableStretch && typeof renderLabel !== 'function' && (
          // Invisible duplicate of options to make the label as wide as its options need space
          <div className="custom-select-box__label-stretcher">
            <div>
              {!placeholder && label}
              <Icon symbol={arrowIcon} className="ml-2" />
            </div>
            {options.map(({ label }, index) => (
              <div key={index}>
                {label}
                <Icon symbol={arrowIcon} className="ml-2" />
              </div>
            ))}
          </div>
        )}
      </InlineButton>

      <div role="listbox" className="custom-select-box__dropdown">
        {typeof renderOptions === 'function' ? (
          renderOptions(renderOpts)
        ) : multiple ? (
          <MultiCheckbox
            className="mx-3"
            onChange={handleChange}
            data={data}
            options={options}
            hideErrors={true}
          />
        ) : (
          <ul>
            {options.map(({ label, value, disabled }, index) => {
              const cls = classNames('custom-select-box__option', {
                'custom-select-box__option--active': valueState === value,
              })
              return (
                <li key={index}>
                  <InlineButton
                    className={cls}
                    onClick={handleChange}
                    disabled={disabled}
                    name={data.name}
                    value={value}
                  >
                    {label}
                  </InlineButton>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <ErrorMessage message={data.error} />
    </div>
  )
}

CustomSelectbox.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.any,
    error: PropTypes.any,
  }).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  hidden: PropTypes.bool,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  renderLabel: PropTypes.func,
  renderOptions: PropTypes.func,
  arrowIcon: PropTypes.string,
  align: PropTypes.oneOf(['left', 'center', 'right']),
  disableStretch: PropTypes.bool,
}

export default CustomSelectbox
